import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Badge,
  HStack,
  VStack,
  Grid,
  GridItem,
  Icon,
  Button,
} from '@chakra-ui/react';
import { FaArrowRight } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';

import Wave from './Wave';
import { MotionBox } from './motion';

const PageHero = ({
  eyebrow,
  title,
  accent,
  description,
  image,
  icon,
  badges = [],
  cta,
  accentColor = 'gold.300',
  gradient = 'linear(120deg, rgba(5,42,27,0.97) 0%, rgba(8,28,52,0.92) 100%)',
  imagePosition = 'center',
}) => (
  <Box color="white" pt={{ base: 22, md: 28 }} pb={{ base: 16, md: 24 }} position="relative" overflow="hidden" bg="brand.900">
    {/* Background */}
    <Box position="absolute" inset={0}>
      {image && (
        <Box
          position="absolute"
          inset={0}
          bgImage={`url(${image})`}
          bgSize="cover"
          bgPosition={imagePosition}
          opacity={0.25}
        />
      )}
      <Box position="absolute" inset={0} bgGradient={gradient} />
      <Box position="absolute" inset={0} className="bg-grid" opacity={0.32} />
    </Box>

    <Box
      position="absolute"
      top="-130px"
      right="-90px"
      w="400px"
      h="400px"
      borderRadius="full"
      bgGradient="radial(circle at center, rgba(20,138,85,0.32), transparent 70%)"
      className="animate-float-slow"
    />

    <Container maxW="7xl" position="relative" zIndex={1}>
      <Grid
        templateColumns={icon ? { base: '1fr', lg: '1.15fr 0.85fr' } : '1fr'}
        gap={{ base: 10, lg: 12 }}
        alignItems="center"
      >
        <GridItem>
          <VStack align="start" spacing={6} maxW={icon ? '2xl' : '3xl'}>
            <Box
              bg="whiteAlpha.200"
              backdropFilter="blur(14px)"
              color="white"
              px={5}
              py={2}
              borderRadius="full"
              fontSize="xs"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.16em"
              border="1px solid"
              borderColor="whiteAlpha.300"
            >
              {eyebrow}
            </Box>

            <Heading
              as="h1"
              fontSize={{ base: '2xl', sm: '3xl', lg: '4xl' }}
              fontWeight="800"
              lineHeight="1.12"
              letterSpacing="-0.03em"
            >
              {title}
              {accent && (
                <Text as="span" display="block" mt={2} color={accentColor} fontWeight="800">
                  {accent}
                </Text>
              )}
            </Heading>

            <Text fontSize={{ base: 'md', md: 'lg' }} color="whiteAlpha.800" lineHeight="1.8">
              {description}
            </Text>

            {badges.length > 0 && (
              <HStack spacing={3} flexWrap="wrap">
                {badges.map((badge) => (
                  <Badge
                    key={badge.label}
                    bg="whiteAlpha.200"
                    color="white"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    px={4}
                    py={1.5}
                    fontSize="sm"
                  >
                    {badge.label}
                  </Badge>
                ))}
              </HStack>
            )}

            {cta && (
              <HStack spacing={4} pt={2} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to={cta.to}
                  size="lg"
                  bg={cta.accent || 'brand.500'}
                  color={cta.accent ? 'navy.900' : 'white'}
                  rightIcon={<FaArrowRight />}
                >
                  {cta.label}
                </Button>
                {cta.secondary && (
                  <Button as={RouterLink} to={cta.secondary.to} size="lg" variant="ghostOnDark">
                    {cta.secondary.label}
                  </Button>
                )}
              </HStack>
            )}
          </VStack>
        </GridItem>

        {icon && (
          <GridItem display={{ base: 'none', lg: 'block' }}>
            <MotionBox
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Icon
                as={icon}
                boxSize={{ base: '160px', lg: '260px' }}
                color="whiteAlpha.200"
                transform="rotate(-8deg)"
              />
            </MotionBox>
          </GridItem>
        )}
      </Grid>
    </Container>

    <Wave fill="white" height={64} />
  </Box>
);

export default PageHero;
